import type { ComponentType } from 'react';
import { notFound, useLoaderData } from '@tanstack/react-router';
import { MDXProvider } from '@mdx-js/react';

import { Link } from '@/core/i18n/navigation';
import { envConfigs } from '@/config';
import { contentDate } from '@/lib/content-date';
import { pageSchema, seoHead } from '@/lib/seo';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { Breadcrumb } from '@/components/control-resonant/breadcrumb';
import { StructuredData } from '@/components/control-resonant/structured-data';

type PageMeta = {
  title: string;
  description: string;
  updated_at: string;
};

type PageModule = {
  default: ComponentType;
  meta: PageMeta;
};

// Eagerly bundle the static content pages (small legal/info MDX files).
// Keys are absolute from the project root.
const pages = import.meta.glob<PageModule>('/src/content/pages/*.mdx', {
  eager: true,
});

function loadPage(slug: string, locale: string): PageModule | null {
  return pages[`/src/content/pages/${slug}.${locale}.mdx`] ?? null;
}

type LoaderData = { meta: PageMeta; slug: string; locale: string };

// Shared route options for static MDX pages. Each page gets its own
// explicit route file (e.g. privacy-policy.tsx) so static segments
// always outrank dynamic ones — add a new page by creating the MDX
// content plus a thin route file using this factory.
export function staticPageRouteOptions(slug: string) {
  return {
    loader: (): LoaderData => {
      const locale = getLocale();
      const page = loadPage(slug, locale);
      if (!page) throw notFound();
      return { meta: page.meta, slug, locale };
    },
    head: ({ loaderData }: { loaderData?: LoaderData }) => {
      if (!loaderData) return {};
      const { meta } = loaderData;
      return seoHead({
        title: meta.title,
        description: meta.description,
        url: `/${slug}`,
        locale: loaderData.locale,
        indexable: false,
      });
    },
    component: StaticPage,
  };
}

function StaticPage() {
  const { meta, slug, locale } = useLoaderData({
    strict: false,
  }) as LoaderData;

  const page = loadPage(slug, locale)!;
  const Content = page.default;

  return (
    <article>
      <Breadcrumb
        label={locale === 'zh' ? '面包屑导航' : 'Breadcrumb'}
        items={[
          { label: locale === 'zh' ? '首页' : 'Home', href: '/' },
          { label: meta.title, href: '/' + slug },
        ]}
      />
      <StructuredData
        data={pageSchema({
          title: meta.title,
          description: meta.description,
          url: '/' + slug,
          schema: 'WebPage',
          locale,
        })}
      />
      <header className="border-border mb-6 border-b pb-5">
        <h1 className="text-foreground text-3xl font-semibold tracking-tight md:text-4xl">
          {meta.title}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">{meta.description}</p>
        <p className="text-muted-foreground mt-2 text-xs">
          {m['common.pages.last_updated']()}:{' '}
          <time dateTime={meta.updated_at}>
            {contentDate(meta.updated_at, locale)}
          </time>
        </p>
      </header>
      <div className="text-foreground/90 text-[15px] leading-7">
        <MDXProvider
          components={{
            a: ({
              href = '',
              children,
              ...props
            }: React.ComponentProps<'a'>) => (
              <Link href={href} {...props}>
                {children}
              </Link>
            ),
          }}
        >
          <Content />
        </MDXProvider>
      </div>
    </article>
  );
}
